import * as e from 'emerge'
import * as a from '@mitranim/js/all.mjs'
import * as um from './util-misc.mjs'

/*
TODO: move iteration functions, particularly those that take callbacks,
to `util-iter.mjs` for consistency with `js/iter.mjs`.
*/

/*
Example input:

  src = [10, 20, 30]
  fun = () => -10

Example output:

  [10, -10, 20, -10, 30]
*/
export function intersperse(src, fun) {
  a.reqFun(fun)
  const buf = []
  for (src of a.arr(src)) buf.push(src, fun())
  buf.pop()
  return buf
}

export function intersperseN(src, size, fun) {
  src = a.values(src)
  a.reqIntPos(size)
  a.reqFun(fun)

  const buf = []

  let ind = 0
  while (ind < src.length) {
    buf.push(...src.slice(ind, ind + size))
    buf.push(fun(ind/size))
    ind += size
  }
  return buf
}

export function trimList(src) {return a.filter(src, a.isSome)}
export function trimDict(src) {return a.pick(src, a.isSome)}

/*
Differences from `a.vac`:

  * Supports all collection types.
  * Doesn't check if collection elements are empty.

TODO consolidate.
*/
export function vacate(src) {return a.only(src, a.hasLen)}

export function vacDict(src) {
  return a.isSome(src) ? vacate(trimDict(src)) : undefined
}

export function vacOf(...val) {return a.vac(a.compact(val))}

export function mapVac(src, fun) {return a.vac(a.mapCompact(src, fun))}

export function indexByEqual(src, val) {
  return a.findIndex(src, a.bind(e.equal, val))
}

export function someEquiv(values, val) {
  return indexByEquiv(values, val) !== -1
}

export function indexByEquiv(src, val) {
  return a.findIndex(src, a.bind(equiv, val))
}

export function toggleEqual(vals, val) {
  const ind = indexByEqual(vals, val)
  return ind >= 0 ? e.remove(vals, ind) : a.append(vals, val)
}

/*
Same as `a.is` or `e.is` but considers `null` and `undefined` equal. This is
not the default behavior because sometimes it's undesirable.
*/
export function eq(left, right) {
  return a.is(left, right) || (a.isNil(left) && a.isNil(right))
}

/*
Same as Emerge's `e.equal`, but considers `null` and `undefined` equal. This is
not the default behavior because sometimes it's undesirable.
*/
export function equiv(left, right) {
  return eq(left, right) || e.equalBy(left, right, equiv)
}

export function timesBetween(start, end, fun, ...args) {
  a.reqInt(start)
  a.reqInt(end)
  a.reqFun(fun)

  const out = []
  while (start < end) {
    out.push(fun(start, ...args))
    start += 1
  }
  return out
}

/*
Allows to edit an array in-place, in a single traversal, per-element. The
provided function receives every element and MAY return its replacement. If the
function returns nil, no changes are made. If the function returns a list, that
list is spliced into the original array, replacing the element. These mutations
have no effect on the iteration order; each original element is visited, but
replacements are not. Note that returning `[]` causes the element to be removed
with no replacement.
*/
export function arrEdit(arr, fun, ...args) {
  if (a.isNil(arr)) return
  a.reqArr(arr)

  let ind = -1
  while (++ind < arr.length) {
    const val = fun(arr[ind], ...args)

    if (a.isNil(val)) continue

    if (a.isList(val)) {
      arr.splice(ind, 1, ...val)
      ind -= 1
      ind += val.length
      continue
    }

    throw Error(`unrecognized non-nil, non-array output in array edit: ${String(val)}`)
  }
}

// TODO: use primary keys and maps.
export function setAddBy(left, right, equal) {
  a.reqFun(equal)
  const out = []
  for (const val of a.concat(left, right)) {
    if (!includesBy(out, val, equal)) out.push(val)
  }
  return out
}

function includesBy(src, val, equal) {
  return a.some(src, a.bind(equal, val))
}

// TODO: use primary keys and maps.
export function setSubBy(left, right, equal) {
  left = a.arr(left)
  right = a.arr(right)
  a.reqFun(equal)
  return a.reject(left, val => includesBy(right, val, equal))
}

// TODO remove after converting query-related code to `a.Url`/`a.Query`.
export function alwaysList(val) {
  if (a.isNil(val)) return []
  if (a.isList(val)) return val
  if (a.hasLen(val) && !a.isStruct(val)) return a.arr(val)
  return [val]
}

// Opposite of `alwaysList`. Unwraps single-element lists recursively.
export function unwrapList(val) {
  while (a.isList(val)) {
    if (!val.length) return undefined
    if (val.length === 1) val = val[0]
  }
  return val
}

// TODO better name.
export function toListOpt(val) {return a.isNil(val) ? undefined : [val]}

export function alwaysHead(val) {
  return a.isList(val) ? a.head(val) : val
}

export function toMeaningfulList(value) {
  return um.isMeaningless(value) ? [] : alwaysList(value)
}

export function conserve(prev, next) {
  return e.equal(prev, next) ? prev : next
}

export function put(prev, next) {return e.putIn(prev, [], next)}

export function pickByPatch(src, patch) {
  return a.pickKeys(src, a.keys(patch))
}

export function adjoin(coll, val) {
  coll = a.arr(coll)
  return coll.includes(val) ? coll : a.append(coll, val)
}

// TODO move to `@mitranim/js`.
export function union(...val) {
  const tar = new Set()
  for (val of val) for (val of a.arr(val)) tar.add(val)
  return tar
}

export function uniq(val) {return a.arr(a.setFrom(val))}

// TODO consider moving to `@mitranim/js`.
export function sumBy(src, fun) {
  a.reqFun(fun)
  let out = 0
  for (src of a.values(src)) out += a.laxFin(fun(src))
  return out
}

export function countWhile(src, fun) {
  a.reqFun(fun)
  let ind = 0
  for (src of a.values(src)) {
    if (!fun(src)) break
    ind++
  }
  return ind
}

export function setsIntersect(one, two) {
  one = a.setFrom(one)
  two = a.setFrom(two)

  if (!one.size) return false
  if (!two.size) return false

  for (const val of one) if (two.has(val)) return true
  for (const val of two) if (one.has(val)) return true
  return false
}

export function isSuperset(one, two) {
  one = a.setFrom(one)
  two = a.setFrom(two)

  if (!two.size) return true
  if (!one.size) return false

  for (const val of two) if (!one.has(val)) return false
  return true
}

/*
Experimental. Takes a list of funcs and returns a list of the results of the
first N functions that return truthy results. Stops evaluation as soon as one
of the functions returns a falsy result.
*/
export function collectWhile(...val) {
  a.reqArrOf(val, a.isFun)
  const out = []
  for (val of val) if ((val = val())) {out.push(val)} else {break}
  return out
}

export function removedMut(tar, val) {
  const ind = a.optArr(tar)?.indexOf(val)
  return ind >= 0 && (tar.splice(ind, 1), true)
}

export function replacedMut(tar, val) {
  const ind = a.optArr(tar)?.indexOf(val)
  return ind >= 0 && ((tar[ind] = val), true)
}

// Unused, TODO remove.
export function proportion(src, fun) {
  src = a.arr(src)
  a.reqFun(fun)
  const length = src.length
  return length ? a.count(src, fun) / length : undefined
}

/*
TODO better name. This name can be confusing because in many retard languages
"long" means "64-bit integer". Also 2 elements isn't particularly long.
*/
export function isLong(val) {return a.len(val) > 1}

export function findInst(src, cls) {
  a.reqCls(cls)
  return a.find(src, function test(val) {return a.isInst(val, cls)})
}

export function filterInsts(src, cls) {
  a.reqCls(cls)
  return a.filter(src, function test(val) {return a.isInst(val, cls)})
}

export function indexByPk(src, pk) {
  a.reqPk(pk)
  return a.findIndex(src, function test(val) {return a.pk(val) === pk})
}

/*
Example:

  src = [10, 20, 30, 40, 50, 60]
  len = 3
  out = [[10, 20, 30], [40, 50, 60]]
*/
export function chunks(src, len) {
  src = a.arr(src)
  a.reqIntPos(len)

  const count = src.length / len

  if (!a.isNat(count)) {
    throw Error(`unable to divide collection of size ${src.length} into chunks of size ${len}`)
  }

  return a.times(count, function chunk(ind) {
    return src.slice((len * ind), (len * (ind + 1)))
  })
}
